import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import DashboardMiniCal from './DashboardMiniCal';

function UnifiedDashboardCalendar({ authUser, vendorProfile, userEvents, setTab }) {
  const [hostEvents, setHostEvents] = useState(userEvents || []);
  const [vendorBookings, setVendorBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!authUser) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      // Fetch host events if not passed
      if (!userEvents || userEvents.length === 0) {
        const { data } = await supabase.from('events').select('*')
          .or(`user_id.eq.${authUser.id},contact_email.ilike.${authUser.email}`)
          .order('date',{ascending:true});
        if (mounted && data) setHostEvents(data);
      }
      // Fetch vendor bookings
      if (vendorProfile) {
        let allReqs = [];
        if (vendorProfile.id) { const { data } = await supabase.from('booking_requests').select('*').eq('vendor_id', vendorProfile.id); if (data) allReqs = data; }
        if (allReqs.length === 0 && vendorProfile.contact_email) { const { data } = await supabase.from('booking_requests').select('*').ilike('host_email', vendorProfile.contact_email); if (data) allReqs = data; }
        if (allReqs.length === 0 && vendorProfile.name) { const { data } = await supabase.from('booking_requests').select('*').ilike('vendor_name', vendorProfile.name); if (data) allReqs = data; }
        const seen = new Set(); allReqs = allReqs.filter(r => { if(seen.has(r.id)) return false; seen.add(r.id); return true; });
        if (mounted) setVendorBookings(allReqs);
      }
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [authUser, vendorProfile, userEvents]);

  const todayStr = new Date().toISOString().split('T')[0];
  const fmtD = (d) => d ? new Date(d+'T12:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

  // Build unified dates map for DashboardMiniCal
  const calDates = {};
  hostEvents.forEach(e => { if (e.date) calDates[e.date] = { type: 'event' }; });
  vendorBookings.forEach(b => {
    if (b.event_date) {
      if (b.status === 'accepted') calDates[b.event_date] = { type: 'accepted' };
      else if (b.status === 'pending' && !calDates[b.event_date]) calDates[b.event_date] = { type: 'pending' };
    }
  });

  const confirmed = vendorBookings.filter(b => b.status==='accepted' && (b.event_date||'')>=todayStr).sort((a,b)=>(a.event_date||'').localeCompare(b.event_date||''));
  const pending = vendorBookings.filter(b => b.status==='pending' && (b.event_date||'')>=todayStr).sort((a,b)=>(a.event_date||'').localeCompare(b.event_date||''));
  const upcomingEvents = hostEvents.filter(e => (e.date||'')>=todayStr).sort((a,b)=>(a.date||'').localeCompare(b.date||''));

  const hasData = hostEvents.length > 0 || vendorBookings.length > 0;

  return (
    <div style={{marginBottom:24}}>
      <h3 style={{fontFamily:'Playfair Display,serif',fontSize:20,marginBottom:16}}>My Calendar</h3>
      {loading ? <div style={{color:'#a89a8a',padding:16}}>Loading...</div> : !hasData ? (
        <div style={{background:'#f5f0ea',border:'1px solid #e8ddd0',borderRadius:10,padding:'20px',textAlign:'center'}}>
          <div style={{fontSize:13,color:'#7a6a5a'}}>No events or bookings yet.</div>
        </div>
      ) : (
        <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
          {/* Mini calendar */}
          <div style={{flex:'0 0 220px',background:'#1a1410',borderRadius:10,padding:16}}>
            <DashboardMiniCal dates={calDates} onDateClick={(s)=>setExpanded(expanded===s?null:s)} />
            <div style={{display:'flex',gap:6,justifyContent:'center',marginTop:8,flexWrap:'wrap'}}>
              {hostEvents.length > 0 && <div style={{display:'flex',alignItems:'center',gap:3,fontSize:8,color:'#a89a8a'}}><div style={{width:7,height:7,borderRadius:2,background:'#c8a84b'}}/> Events</div>}
              {confirmed.length > 0 && <div style={{display:'flex',alignItems:'center',gap:3,fontSize:8,color:'#a89a8a'}}><div style={{width:7,height:7,borderRadius:2,background:'#1a6b3a'}}/> Confirmed</div>}
              {pending.length > 0 && <div style={{display:'flex',alignItems:'center',gap:3,fontSize:8,color:'#a89a8a'}}><div style={{width:7,height:7,borderRadius:2,background:'#c8a84b'}}/> Pending</div>}
            </div>
            <button onClick={()=>setTab('my-calendar')} style={{display:'block',width:'100%',background:'none',border:'1px solid #c8a84b',color:'#c8a84b',borderRadius:6,padding:'6px',fontSize:11,fontWeight:600,cursor:'pointer',marginTop:10,fontFamily:'DM Sans,sans-serif'}}>Full Calendar →</button>
          </div>
          {/* Upcoming list */}
          <div style={{flex:1,minWidth:280}}>
            {upcomingEvents.length > 0 && (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:'#7a5a10',marginBottom:6}}>My Events (Host)</div>
                {upcomingEvents.slice(0,4).map(e => (
                  <div key={e.id} style={{background:'#fdf4dc',border:'1px solid #ffd966',borderRadius:8,padding:'8px 12px',marginBottom:4}}>
                    <div style={{fontWeight:600,fontSize:12,color:'#1a1410'}}>{e.event_name}</div>
                    <div style={{fontSize:10,color:'#7a5a10'}}>{fmtD(e.date)} · {e.event_type} · {e.status==='approved'?'Live':e.status}</div>
                  </div>
                ))}
              </div>
            )}
            {confirmed.length > 0 && (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:'#1a6b3a',marginBottom:6}}>Confirmed Bookings</div>
                {confirmed.slice(0,4).map(b => (
                  <div key={b.id} onClick={()=>setExpanded(expanded===b.id?null:b.id)} style={{background:'#d4f4e0',border:'1px solid #b8e8c8',borderRadius:8,padding:'8px 12px',marginBottom:4,cursor:'pointer'}}>
                    <div style={{fontWeight:600,fontSize:12,color:'#1a1410'}}>{b.event_name||'Event'}</div>
                    <div style={{fontSize:10,color:'#2d7a50'}}>{fmtD(b.event_date)} · Zip {b.event_zip||'—'}</div>
                    {expanded===b.id && <div style={{marginTop:4,fontSize:10,color:'#1a6b3a',borderTop:'1px solid #b8e8c8',paddingTop:4}}>Host: {b.host_name||'—'}{b.host_email?` · ${b.host_email}`:''}</div>}
                  </div>
                ))}
              </div>
            )}
            {pending.length > 0 && (
              <div>
                <div style={{fontSize:12,fontWeight:700,color:'#7a5a10',marginBottom:6}}>Pending Applications</div>
                {pending.slice(0,4).map(b => (
                  <div key={b.id} style={{background:'#fdf4dc',border:'1px solid #ffd966',borderRadius:8,padding:'8px 12px',marginBottom:4}}>
                    <div style={{fontWeight:600,fontSize:12,color:'#1a1410'}}>{b.event_name||'Event'}</div>
                    <div style={{fontSize:10,color:'#7a5a10'}}>{fmtD(b.event_date)} · Awaiting response</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default UnifiedDashboardCalendar;
